/* eslint-disable fp/no-mutation */
import React, { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Analytics as SentimentIcon,
  MenuBook as CourseIcon,
  Science as SyntheticIcon,
  Help as UnknownIcon,
  Description as DefaultIcon,
} from '@mui/icons-material'
import JsonView from '@uiw/react-json-view'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import dayjs from 'dayjs'
import {
  fetchMotherSessionLore,
  selectMotherSessionLore,
  selectMotherSessionLoreStatus,
} from '../mother.slice'
import type { MotherLoreFile, LoreType } from '../types'

type LoreTabProps = {
  correlationID: string
}

let getLoreTypeColor = (
  type: LoreType,
): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (type) {
    case 'sentiment-analysis':
      return 'info'
    case 'course-lookup':
      return 'primary'
    case 'synthetic-data':
      return 'secondary'
    case 'unknown':
      return 'warning'
    default:
      return 'secondary'
  }
}

let getLoreTypeIcon = (type: LoreType) => {
  switch (type) {
    case 'sentiment-analysis':
      return <SentimentIcon fontSize="small" />
    case 'course-lookup':
      return <CourseIcon fontSize="small" />
    case 'synthetic-data':
      return <SyntheticIcon fontSize="small" />
    case 'unknown':
      return <UnknownIcon fontSize="small" />
    default:
      return <DefaultIcon fontSize="small" />
  }
}

let formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  let k = 1024
  let sizes = ['B', 'KB', 'MB', 'GB']
  let i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

let copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
}

let downloadFile = (content: string, filename: string) => {
  let blob = new Blob([content], { type: 'application/json' })
  let url = window.URL.createObjectURL(blob)
  let a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(url)
  document.body.removeChild(a)
}

export default ({ correlationID }: LoreTabProps): ReactElement => {
  let dispatch = useAppDispatch()
  let loreFiles = useAppSelector(selectMotherSessionLore(correlationID))
  let loreStatus = useAppSelector(selectMotherSessionLoreStatus(correlationID))
  let [expandedPanels, setExpandedPanels] = useState<string[]>([])

  useEffect(() => {
    if (loreStatus === 'uninitialized') {
      dispatch(fetchMotherSessionLore(correlationID))
    }
  }, [dispatch, correlationID, loreStatus])

  let handlePanelChange = (panel: string) => (_: any, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedPanels((prev) => [...prev, panel])
    } else {
      setExpandedPanels((prev) => prev.filter((p) => p !== panel))
    }
  }

  if (loreStatus === 'pending') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (loreStatus === 'rejected') {
    return (
      <Alert severity="error">
        Failed to load lore files. Please try again.
      </Alert>
    )
  }

  if (!loreFiles || loreFiles.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
        <Typography>No lore files available</Typography>
      </Box>
    )
  }

  // Group lore files by type
  let loreByType = loreFiles.reduce(
    (acc: any, file: any) => {
      if (!acc[file.type]) {
        acc[file.type] = []
      }
      return {
        ...acc,
        [file.type]: [...(acc[file.type] || []), file],
      }
    },
    {} as Record<LoreType, MotherLoreFile[]>,
  )

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Lore Data ({loreFiles.length})
      </Typography>
      {Object.entries(loreByType).map(([type, files]) => (
        <Paper key={type} sx={{ mb: 2, bgcolor: 'background.paperDark' }}>
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getLoreTypeIcon(type as LoreType)}
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {type
                  .replace('-', ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </Typography>
              <Chip
                label={(files as any[]).length}
                color={getLoreTypeColor(type as LoreType)}
                size="small"
              />
            </Box>
          </Box>

          {(files as any[]).map((file: any) => (
            <Accordion
              key={file.executionID}
              expanded={expandedPanels.includes(file.executionID)}
              onChange={handlePanelChange(file.executionID)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    pr: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {file.fileName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(file.size)} •{' '}
                      {dayjs(file.lastModified).fromNow()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {file.executionID.slice(0, 8)}...
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copyToClipboard(file.content)}
                      variant="outlined"
                      aria-label={`Copy ${file.fileName} content to clipboard`}
                    >
                      Copy
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => downloadFile(file.content, file.fileName)}
                      variant="outlined"
                      aria-label={`Download ${file.fileName}`}
                    >
                      Download
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: 'background.default',
                      p: 2,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      maxHeight: 400,
                      overflowY: 'auto',
                    }}
                  >
                    <JsonView
                      value={(() => {
                        try {
                          return typeof file.content === 'string'
                            ? JSON.parse(file.content)
                            : file.content
                        } catch {
                          return { content: file.content }
                        }
                      })()}
                      style={
                        {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          fontSize: '14px',
                          '--w-rjv-color': '#ffffff',
                          '--w-rjv-key-string': '#9db7ff',
                          '--w-rjv-background-color': 'transparent',
                        } as any
                      }
                      displayDataTypes={false}
                      displayObjectSize={false}
                      enableClipboard={true}
                    />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      ))}
    </Box>
  )
}
